import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import DateConverter from "@heartinz/date_and_time_processor";
import CurrencyConverter from "@heartinz/currency_subunit_converter";

import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import Messages from "../../../constants/Messages";
import handleError from "../../../constants/handleError";
import { useAuth } from "../../../context/AuthProvider";
import { useContextApi } from "../../../context/ContextApi";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import GetServerLiveTime from "../../../constants/GetServerLiveTime";
import { CalendarIcon } from "@mui/x-date-pickers";
import { AddIcon, XIcon } from "../../../constants/Icons";

const AddExpense = ({
  isActive,
  isOpen,
  onClose,
  setExpenseData,
  expenseDate1,
  expenseDate,
  setTotalExpense,
  setExpenseDate,
  handleExpenseList,
  listExpense,
  setTotalItems,
  currentDate,
}) => {
  const { useEscapeKey } = useContextApi();
  const [formData, setFormData] = useState({
    date: expenseDate1,
    title: "",
    description: "",
    paymode: "",
    amount: "",
  });
  const datePickerRef = useRef(null);
  const serverTime = GetServerLiveTime();
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isBtnLoading, setBtnLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const { isUser } = useAuth();

  const [errors, setErrors] = useState({});
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const storeTitleRef = useRef(null);
  const modeSelectedRef = useRef(null);
  const amountRef = useRef(null);
  const buttonRef = useRef();
  const desRef = useRef();
  const suggestionRef = useRef([]);

  const formatDate = (inputDate) => {
    const date = inputDate || expenseDate;

    if (!(date instanceof Date)) {
      const parsed = new Date(date);
      if (isNaN(parsed)) return "";
      return formatDate(parsed);
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const handleCashDate = (selectedDate) => {
    if (!selectedDate) return;

    const isoDateTime = selectedDate.toISOString();
    setFormData((prev) => ({ ...prev, date: isoDateTime }));

    storeTitleRef.current?.focus();
    if (formData.title.trim()) {
      setShowSuggestions(false);
    }
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.title.trim()) {
      errs.title = Messages.ERROR.EMPTY_TITLE;
    } else if (
      formData.title.trim().length < 2 ||
      formData.title.trim().length > 100
    ) {
      errs.title = "Title name must be between 2-100 characters.";
    } else if (!formData.amount) {
      errs.amount = Messages.ERROR.AMOUNT_EMPTY;
    } else if (!formData.paymode) {
      errs.paymode = Messages.ERROR.PAYMODE_EMPTY;
    }

    setErrors(errs);

    if (Object.keys(errs).length > 0) {
      const firstError = Object.keys(errs)[0];
      setTimeout(() => {
        if (firstError === "title") storeTitleRef.current?.focus();
        else if (firstError === "amount") amountRef.current?.focus();
        else if (firstError === "paymode") modeSelectedRef.current?.focus();
      }, 10);
      return false;
    }
    return Object.keys(errs).length === 0;
  };

  const handleChange = (field, value) => {
    setErrors("");
    if (field === "title" || field === "description") {
      value = value.replace(/^\s+/, "").replace(/\s{2,}/g, " ");
    }
    if (field === "title") {
      setFormData((prev) => ({ ...prev, title: value }));
      const filtered = suggestions.filter((s) =>
        s.title.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(true);
      return;
    }

    if (field === "amount") {
      const regex = /^\d+(\.\d{0,2})?$/;
      if (value === "" || regex.test(value)) {
        setFormData((prev) => ({ ...prev, [field]: value }));
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAdd = async (newData, currentdate) => {
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const data = {
        title: newData.title,
        ...(newData.description && { description: newData.description }),
        expensedate: DateConverter.convertToTimeZone(
          newData.expensedate,
          isUser.timezone,
        ),
        amount: CurrencyConverter.toSubunit(newData.amount, "INR"),
        paymode: newData.paymode,
      };

      const response = await AxiosInstance.post(
        `module/expense/add-expense`,
        data,
      );
      showAlert("success", Messages.SUCCESS.EXPENSE_ADDED);
      const isSameExpenseDate =
        DateConverter.ISOToDisplayFormat(
          DateConverter.convertToTimeZone(
            expenseDate1 || expenseDate,
            isUser.timezone,
          ),
        ).date ===
        DateConverter.ISOToDisplayFormat(
          DateConverter.convertToTimeZone(newData.expensedate, isUser.timezone),
        ).date;

      if (typeof handleExpenseList === "function") {
        if (!isSameExpenseDate) {
          setExpenseDate(
            DateConverter.convertToTimeZone(
              newData.expensedate,
              isUser?.timezone,
            ),
          );
          await listExpense(newData.expensedate);
        } else {
          setExpenseData((prev) => [response.data.results, ...prev]);
          setTotalExpense(
            (prevTotal) => prevTotal + response.data.results.amount,
          );
          setTotalItems((prev) => prev + 1);
        }
      }
      handleClose();
      return true;
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const trimmedData = {
      ...formData,
      expensedate: formatDate(formData.date),
      title: formData.title.trim(),
      description: formData.description.trim(),
    };
    handleAdd(trimmedData, formatDate(formData.date));
  };

  const handleClose = () => {
    setErrors({});
    setFormData({ title: "", description: "", paymode: "", amount: "" });
    onClose();
  };
  useEscapeKey(handleClose);
  const stopEventPropagation = (e) => e.stopPropagation();

  const fetchSuggestions = async () => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(
        `module/expense/list-expense-suggestion`,
      );
      setSuggestions(response.data.results);
      setShowSuggestions(true);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    }
  };

  const handleFieldInteraction = (
    fieldName,
    eventType = "focus",
    keyEvent = null,
  ) => {
    // setErrors((prev) => ({ ...prev, [fieldName]: "" }));

    if (fieldName !== "title") {
      setShowSuggestions(false);
    }

    if (keyEvent?.key === "Enter") {
      keyEvent.preventDefault();
      if (fieldName === "title" && formData.title.trim()) {
        desRef.current?.focus();
        setShowSuggestions(false);
      } else if (fieldName === "description") {
        amountRef.current?.focus();
      } else if (fieldName === "amount") {
        if (formData.amount.trim()) {
          modeSelectedRef.current?.focus();
        } else {
          setErrors((prev) => ({
            ...prev,
            amount: Messages.ERROR.AMOUNT_EMPTY,
          }));
        }
      }
    }
  };
  const handlePaymodeKeyDown = (e) => {
    if (e.key === "Enter" && formData.paymode.trim()) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && suggestionRef.current[highlightedIndex]) {
      suggestionRef.current[highlightedIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    if (!isOpen || !isActive) return;

    fetchSuggestions();

    const timer = setTimeout(() => {
      storeTitleRef.current?.focus();
      storeTitleRef.current?.select();
    }, 80); // small delay for modal render

    return () => clearTimeout(timer);
  }, [isOpen, isActive]);


  useEffect(() => {
    if (showSuggestions) {
      setFilteredSuggestions(suggestions);
    }
  }, [suggestions, showSuggestions]);

  const isValidDate = (date) => date instanceof Date && !isNaN(date);
  const expenseDateObj = new Date(formData.date || expenseDate);

  if (!isOpen) return null;

  return (
    <div>
      <div className="modal-backdrop show"></div>
      <div
        className="modal show d-block"
        id="StoreModal"
        tabIndex="0"
        aria-modal="true"
        role="dialog"
      >
        <div
          className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
          // onClick={stopEventPropagation}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title">Add Expense</h6>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="card w-100 radius-10 mb-0 shadow-none">
                <div className="card-body p-0">
                  <div className="row g-3">
                    <div className="col-lg-6">
                      <label className="form-label">
                        {isValidDate(expenseDateObj) &&
                        DateConverter.ISOToDisplayFormat(
                          DateConverter.convertToTimeZone(
                            expenseDate ? expenseDate : currentDate,
                            isUser.timezone,
                          ),
                        ).date ===
                          DateConverter.ISOToDisplayFormat(
                            DateConverter.convertToTimeZone(
                              formData.date || expenseDate,
                              isUser.timezone,
                            ),
                          ).date ? (
                          <>
                            Expense Date -{" "}
                            <span className="bg-dark-text text-white  rounded">
                              Today
                            </span>
                          </>
                        ) : (
                          "Expense Date"
                        )}
                        <span className="text-danger"> *</span>
                      </label>

                      <div className="input-group" ref={datePickerRef}  
                       onClick={(e) => e.stopPropagation()}
                       >
                        <button type="button" className="input-group-text">
                          <CalendarIcon/>
                        </button>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            open={openDatePicker}
                            onOpen={() => setOpenDatePicker(true)}
                            onClose={() => setOpenDatePicker(false)}
                            value={dayjs(formData.date || expenseDate)}
                            maxDate={dayjs(serverTime)}
                            format="DD MMM YYYY"
                            disableOpenPicker
                            onChange={(newValue) => {
                              handleCashDate(newValue);
                              setOpenDatePicker(false);
                            }}
                            slotProps={{
                              textField: {
                                size: "small",
                                className: "form-control cursor-pointer",
                                inputProps: { readOnly: true },
                                onClick: () => setOpenDatePicker(true),
                                onKeyDown: (e) => e.preventDefault(),
                                sx: {
                                  "& .MuiInputBase-input": {
                                    cursor: "pointer",
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <label className="form-label">
                        Title <span className="text-danger">*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control"
                          value={formData.title}
                          onChange={(e) =>
                            handleChange("title", e.target.value)
                          }
                          onFocus={() => {
                            handleFieldInteraction("title");
                            setShowSuggestions(true);
                          }}
                          onMouseDown={() => handleFieldInteraction("title")}
                          onKeyDown={(e) => {
                            const key = e.key;
                            if (key === "ArrowDown") {
                              e.preventDefault();
                              setHighlightedIndex((prev) =>
                                Math.min(
                                  prev + 1,
                                  filteredSuggestions.length - 1,
                                ),
                              );
                            } else if (key === "ArrowUp") {
                              e.preventDefault();
                              setHighlightedIndex((prev) =>
                                Math.max(prev - 1, 0),
                              );
                            } else if (key === "Enter") {
                              if (
                                highlightedIndex >= 0 &&
                                filteredSuggestions[highlightedIndex]
                              ) {
                                e.preventDefault();
                                const selected =
                                  filteredSuggestions[highlightedIndex];
                                setFormData((prev) => ({
                                  ...prev,
                                  title: selected.title,
                                }));
                                setShowSuggestions(false);
                                setErrors("");
                                setHighlightedIndex(-1);
                                setTimeout(() => desRef.current?.focus(), 10);
                              } else {
                                handleFieldInteraction("title", "keydown", e);
                              }
                            } else {
                              handleFieldInteraction("title", "keydown", e);
                            }
                          }}
                          autoComplete="off"
                          ref={storeTitleRef}
                        />
                        {showSuggestions && filteredSuggestions.length > 0 && (
                          <ul className="suggestions-list">
                            {filteredSuggestions.map((item, index) => (
                              <li
                                key={item._id}
                                ref={(e) => (suggestionRef.current[index] = e)}
                                className={`suggestion-item text-muted ${
                                  index === highlightedIndex
                                    ? "highlighted"
                                    : ""
                                }`}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setFormData((prev) => ({
                                    ...prev,
                                    title: item.title,
                                  }));
                                  setShowSuggestions(false);
                                  setErrors("");
                                  setHighlightedIndex(-1);
                                  setTimeout(() => {
                                    desRef.current?.focus();
                                  }, 10);
                                }}
                              >
                                {item.title}{" "}
                                {/* <span className="badge bg-label-retro float-end">
                                  used {item.usageCount}
                                </span> */}
                              </li>
                            ))}
                          </ul>
                        )}

                        {showSuggestions &&
                          filteredSuggestions.length === 0 && (
                            <ul className="suggestions-list">
                              <li className="suggestion-item  text-muted">
                                No suggestions found
                              </li>
                            </ul>
                          )}
                      </div>

                      {errors.title && (
                        <div className="error-message invalid-feedback">
                          {errors.title}
                        </div>
                      )}
                    </div>

                    <div className="col-lg-12">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        maxLength="5000"
                        value={formData.description}
                        rows="3"
                        onChange={(e) =>
                          handleChange("description", e.target.value)
                        }
                        onKeyDown={(e) =>
                          handleFieldInteraction("description", "keydown", e)
                        }
                        ref={desRef}
                      ></textarea>
                      {errors.description && (
                        <div className="error-message invalid-feedback">
                          {errors.description}
                        </div>
                      )}
                    </div>

                    <div className="col-lg-6">
                      <label className="form-label">
                        Amount <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.amount}
                        onChange={(e) => handleChange("amount", e.target.value)}
                        // onFocus={() => handleFieldInteraction("amount")}
                        // onMouseDown={() => handleFieldInteraction("amount")}
                        onKeyDown={(e) =>
                          handleFieldInteraction("amount", "keydown", e)
                        }
                        ref={amountRef}
                      />
                      {errors.amount && (
                        <div className="error-message invalid-feedback">
                          {errors.amount}
                        </div>
                      )}
                    </div>

                    <div className="col-lg-6">
                      <label className="form-label">
                        Payment Mode <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={formData.paymode}
                        onChange={(e) =>
                          handleChange("paymode", e.target.value)
                        }
                        onKeyDown={handlePaymodeKeyDown}
                        ref={modeSelectedRef}
                      >
                        <option value="">Select</option>
                        <option value="CASH">CASH</option>
                        <option value="CARD">CARD</option>
                        <option value="UPI">UPI</option>
                        <option value="OTHER">OTHERS</option>
                      </select>
                      {errors.paymode && (
                        <div className="error-message invalid-feedback">
                          {errors.paymode}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer d-block">
              <button
                type="button"
                className="btn btn-outline-secondary text-left"
                onClick={handleClose}
              >
                <XIcon/> Cancel
              </button>

              {isBtnLoading ? (
                <button className="btn btn-primary pb-0 text-right" disabled>
                  <CircularProgress size={22} className="text-white" />
                </button>
              ) : (
                <button
                  ref={buttonRef}
                  type="button"
                  className="btn btn-primary text-right"
                  onClick={handleSubmit}
                >
                  <AddIcon/> Add
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AddExpense.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

export default AddExpense;
